/* eslint-disable no-console */
import React, { useState, useEffect } from 'react'
import { TrashIcon } from '@heroicons/react/16/solid'
import { TextboxSearch } from '@splunk-iteng/splunk-shared-components'
import {
  ApplicationDataInput,
  useGetAppAttributeQuery,
  useAddAppAttributeMutation
} from '../../graphql/graphql'
import { DEFAULT_CLASS_NAME } from '../../constants/constants'

type Item = {
  id: string
  type: string // 'one to one' | 'one to many' | 'enum'
  key: string
  value: string
  keyValid: boolean
  valueValid: boolean
}

const DROPDOWN_OPTIONS = ['one to one', 'one to many', 'enum'] as const
const VALIDATION_REGEX = /^[a-zA-Z0-9-]+/

const validateInput = (input: string): boolean => {
  return input.length > 0 && VALIDATION_REGEX.test(input)
}

export function AttributeManagement() {
  const [application, setApplication] = useState('')
  const [items, setItems] = useState<Item[]>([])
  const [AttributeManagementMutation] = useAddAppAttributeMutation()
  const { error, data } = useGetAppAttributeQuery({
    fetchPolicy: 'no-cache',
    variables: { appName: application },
    skip: !application || application === ''
  })
  useEffect(() => {
    if (error) alert(error.message)
  }, [error])
  useEffect(() => {
    setItems([])
    if (data?.getAppAttribute?.data) {
      const existingApp = data?.getAppAttribute?.data
      existingApp.forEach((row) => {
        setItems((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            type: row!.attribute_type!,
            key: row!.attribute_key!,
            value: row!.attribute_value!,
            keyValid: true,
            valueValid: true
          }
        ])
      })
    }
  }, [data])
  const isFormValid =
    application.trim().length > 0 &&
    items.length > 0 &&
    items.every((item) => item.keyValid && item.valueValid)
  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'one to one',
        key: '',
        value: '',
        keyValid: false,
        valueValid: false
      }
    ])
  }

  const updateItem = (
    id: string,
    field: 'key' | 'value' | 'type',
    value: string
  ) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item
        if (field === 'type') {
          return { ...item, type: value as Item['type'] }
        }
        if (field === 'key') {
          return {
            ...item,
            key: value,
            keyValid: validateInput(value)
          }
        }
        if (field === 'value') {
          return {
            ...item,
            value,
            valueValid: validateInput(value)
          }
        }
        return item
      })
    )
  }

  const deleteItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
  }

  const preventEnter = async (e: React.FormEvent) => {
    e.preventDefault()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isFormValid) {
      alert('Please correct invalid inputs before submitting.')
      return
    }

    const appData: ApplicationDataInput[] = items.map((row) => {
      return {
        app_name: application,
        app_admin: 'admin1',
        attribute_key: row.key,
        attribute_value: row.value,
        attribute_type: row.type
      }
    })

    await AttributeManagementMutation({
      variables: {
        data: appData
      },
      onCompleted: () => {
        alert('Success')
      },
      onError: (err) => {
        alert(err.message)
      }
    })
  }

  return (
    <div style={{ maxWidth: 600, margin: 'auto', padding: 20 }}>
      <form onSubmit={preventEnter}>
        <div style={{ marginBottom: 16 }}>
          <TextboxSearch
            name="log-filter"
            className={DEFAULT_CLASS_NAME}
            onChange={(e) => setApplication(e.target.value)}
            value={application}
            placeholder="Enter application name"
          />
        </div>
        {items.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 8,
              border: '1px solid #eee',
              padding: 8
            }}
          >
            <select
              value={item.type}
              onChange={(e) => updateItem(item.id, 'type', e.target.value)}
            >
              {DROPDOWN_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Key"
              value={item.key}
              onChange={(e) => updateItem(item.id, 'key', e.target.value)}
              style={{
                borderColor:
                  item.key.length === 0 || item.keyValid ? undefined : 'red',
                outline:
                  item.key.length === 0 || item.keyValid
                    ? undefined
                    : 'red solid 1px'
              }}
            />
            <input
              type="text"
              placeholder="Value"
              value={item.value}
              onChange={(e) => updateItem(item.id, 'value', e.target.value)}
              style={{
                borderColor:
                  item.value.length === 0 || item.valueValid
                    ? undefined
                    : 'red',
                outline:
                  item.value.length === 0 || item.valueValid
                    ? undefined
                    : 'red solid 1px'
              }}
            />
            <button type="button" onClick={() => deleteItem(item.id)}>
              <TrashIcon className="size-6" />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="mt-20 btn btn-primary"
          onClick={addItem}
          style={{ marginRight: 10 }}
        >
          Add Item
        </button>
        <button
          type="button"
          className="mt-20 btn btn-primary"
          onClick={handleSubmit}
          disabled={!isFormValid}
        >
          Submit
        </button>
      </form>
    </div>
  )
}
